// admin/src/pages/GatewayMonitoring/components/EditGatewayHaDialog.js
'use strict';

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Typography,
  Chip,
  Box,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { updateGatewayHaSettings, extractGatewayErrorMessage } from '../gatewayApi';

export default function EditGatewayHaDialog({ open, gateway, onClose, onSuccess, mode }) {
  const [directoryLabel, setDirectoryLabel] = useState('');
  const [priority, setPriority] = useState(2);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');

  const isPrimary = String(gateway?.haRole || gateway?.ha?.role || '').toLowerCase() === 'primary';

  useEffect(() => {
    if (gateway && open) {
      setDirectoryLabel(gateway.directoryLabel || gateway.directory?.label || '');
      const rawPriority = gateway.haPriority || gateway.ha?.priority;
      setPriority(rawPriority !== undefined && rawPriority !== null ? rawPriority : (isPrimary ? 1 : 2));
      setError('');
      setValidationError('');
    }
  }, [gateway, open, isPrimary]);

  if (!gateway) return null;

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setValidationError('');

    const trimmedLabel = (directoryLabel || '').trim();
    if (trimmedLabel.length > 40) {
      setValidationError('Directory label must be 40 characters or less.');
      return;
    }

    const payload = {
      directoryLabel: trimmedLabel,
    };

    if (!isPrimary) {
      const parsedPriority = parseInt(priority, 10);
      if (isNaN(parsedPriority) || parsedPriority < 2) {
        setValidationError('Priority must be an integer >= 2 for secondary gateways.');
        return;
      }
      payload.priority = parsedPriority;
    }

    setLoading(true);
    try {
      await updateGatewayHaSettings(gateway._id, payload);
      onSuccess && onSuccess();
    } catch (err) {
      // Show server error message (especially 409 conflict on duplicate priority) inline
      const serverMsg = err.response?.data?.message || err.response?.data?.error || err.message || err.error;
      const displayMsg = (err.status === 409 || err.statusCode === 409 || err.response?.status === 409 || (serverMsg && String(serverMsg).includes('already assigned')))
        ? serverMsg
        : (serverMsg || extractGatewayErrorMessage(err));
      setError(displayMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose && onClose()}
      maxWidth="sm"
      fullWidth
      data-testid="edit-gateway-ha-dialog"
    >
      <form onSubmit={handleSave}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TuneIcon color="primary" />
          Edit Directory & HA Settings
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" data-testid="ha-settings-error-alert">
                {error}
              </Alert>
            )}
            {validationError && (
              <Alert severity="warning" data-testid="ha-settings-validation-alert">
                {validationError}
              </Alert>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {gateway.name || 'Gateway'}
              </Typography>
              <Chip
                size="small"
                label={isPrimary ? 'PRIMARY' : 'SECONDARY'}
                color={isPrimary ? 'primary' : 'default'}
                sx={{ fontWeight: 700, fontSize: 11 }}
              />
            </Box>

            <TextField
              label="Directory Label"
              fullWidth
              size="small"
              value={directoryLabel}
              onChange={(e) => setDirectoryLabel(e.target.value)}
              placeholder="e.g. HQ Active Directory or DR Site"
              inputProps={{ maxLength: 40, 'data-testid': 'ha-directory-label-input' }}
              helperText={`${(directoryLabel || '').length}/40 characters. Identifies which Active Directory domain/DC this gateway points to.`}
            />

            <TextField
              label="HA Priority"
              fullWidth
              size="small"
              type="number"
              value={isPrimary ? 1 : priority}
              disabled={isPrimary || loading}
              onChange={(e) => setPriority(e.target.value)}
              inputProps={{ min: 2, step: 1, 'data-testid': 'ha-priority-input' }}
              helperText={
                isPrimary
                  ? 'Primary priority is fixed at 1. Use "Promote to Primary" on another gateway to swap priorities.'
                  : 'Failover priority (integer >= 2). Lower numbers take precedence during failover.'
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={onClose}
            disabled={loading}
            data-testid="ha-settings-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            data-testid="ha-settings-save-btn"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <TuneIcon />}
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
